import React from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    IconButton,
    Typography,
    Checkbox,
    Alert,
} from "@material-tailwind/react";
import {
    FormProvider,
    useForm,
    useFormContext,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import MaterialIcon from "components/material-icon";
import RHFRadio from "components/form/rhf-radio";
import { buildPresetInitialValues } from "utils/preset-answer-helpers";
import * as Yup from "yup";

function RegisterCheckbox({ id, name, value, color, checked, ...props }) {
    const { register } = useFormContext();
    const { ref, ...rest } = register(name);
    return (
        <Checkbox
            id={id}
            color={color}
            value={value}
            checked={checked}
            {...rest}
            {...props}
            inputRef={ref}
        />
    );
}

const validationSchemaCustom = Yup.object().shape({
    answer: Yup.array()
        .of(Yup.string())
        .test(
            "at-least-one-answer",
            "Select at least one answer.",
            (answers) => (answers || []).some((value) => !!value),
        ),
});

function CustomResultsForm({
    searchResults,
    question,
    response,
    onSubmit,
    onClose,
    onReport,
}) {
    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            answer: buildPresetInitialValues(
                searchResults.hits,
                response,
                question.answer_type,
            ),
        },
        resolver: yupResolver(validationSchemaCustom),
    });

    const values = methods.watch();

    return (
        <>
            <FormProvider {...methods}>
                <form
                    onSubmit={(e) => {
                        // Portaled dialog forms keep their React-tree
                        // position, so a submit here bubbles into any
                        // host form (the respondent form) and
                        // double-submits. Stop it at the source.
                        e.stopPropagation();
                        return methods.handleSubmit(onSubmit)(e);
                    }}
                >
                    {searchResults.hits.map((result, index) => (
                        <div
                            className="flex font-body border-b border-gray-700 px-2"
                            key={index}
                        >
                            <div className="flex gap-2 font-body grow text-left mt-2">
                                <div className="shrink-0">
                                    {question.answer_type === "single" && (
                                        <RHFRadio
                                            id={`values.answer.0.nanoid`}
                                            name={`answer.0`}
                                            color="blue"
                                            value={
                                                result.document.answer_nanoid
                                            }
                                            checked={
                                                values.answer?.[0] ===
                                                result.document.answer_nanoid
                                            }
                                        />
                                    )}
                                    {question.answer_type === "multiple" && (
                                        <RegisterCheckbox
                                            id={`values.answer.${index}.nanoid`}
                                            name={`answer`}
                                            color="blue"
                                            value={
                                                result.document.answer_nanoid
                                            }
                                            checked={(values.answer || []).includes(
                                                result.document.answer_nanoid,
                                            )}
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 pt-1.5 mr-3">
                                    <span className="text-gray-900">
                                        {result.document.text}
                                    </span>
                                </div>
                            </div>
                            <div className="font-body text-right border-l border-gray-700 my-2">
                                <IconButton
                                    color="red"
                                    type="button"
                                    variant="text"
                                    onClick={() =>
                                        onReport(
                                            result.document.answer_nanoid,
                                        )
                                    }
                                >
                                    <MaterialIcon name="flag" size="2xl" />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                    <div className="font-body my-4 text-center text-black">
                        Click on the flag (
                        <MaterialIcon color="red" name="flag" size="xl" />
                        ) next to an answer to report any abuse.
                    </div>
                    {!!methods.formState.errors?.answer?.message && (
                        <Alert color="red" className="my-2 rounded-lg py-2 px-1 text-sm">
                            {methods.formState.errors.answer.message}
                        </Alert>
                    )}
                    <DialogFooter className="dialog__footer">
                        <Button
                            type="submit"
                            className="btn btn--primary"
                            variant="filled"
                            size="lg"
                            disabled={
                                !values.answer ||
                                (question.answer_type === "single"
                                    ? !values.answer[0]
                                    : values.answer.filter((v) => v).length === 0)
                            }
                        >
                            Confirm
                        </Button>
                        <Button
                            type="button"
                            className="btn btn--secondary"
                            variant="filled"
                            size="lg"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </form>
            </FormProvider>
        </>
    );
}

export default function CustomResultsDialog(props) {
    const handleClose = () => {
        props.handleClose();
    };

    const question = props.question;
    const responses = props.responses;
    const response = props.responses.custom;

    const handleCustomSubmit = async (values) => {
        props.saveSelection(values);
        props.handleClose();
    };

    const reportAnswer = async (answerNanoid) => {
        props.setReportedAnswer(answerNanoid);
        props.setShowConfirmReportDialog(true);
        props.handleClose();
    };

    return (
        <>
            {!!(props.searchResults && props.searchResults.found > 0) && (
                <Dialog
                    open={props.showDialog}
                    handler={handleClose}
                    className="dialog"
                >
                    <DialogHeader className="dialog__header">
                        Results Matching:{" "}
                        {props.searchTerm || "LOADING/UNKNOWN"}
                    </DialogHeader>
                    <DialogBody className="dialog__body">
                        <Typography className="font-body my-2 text-center text-black">
                            Choose one, or more, responses, or go back and add
                            your own.
                        </Typography>
                        <CustomResultsForm
                            searchResults={props.searchResults}
                            question={question}
                            response={response}
                            onSubmit={handleCustomSubmit}
                            onClose={handleClose}
                            onReport={reportAnswer}
                        />
                    </DialogBody>
                </Dialog>
            )}
        </>
    );
}